import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import type { HasChildren } from '../types/components';
import { theme } from '../stitches.config';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';
export type HeightBreakpoint = 'short' | 'tall';

interface ReadonlySize {
  readonly width: number;
  readonly height: number;
  readonly breakpoint: Breakpoint;
  readonly heightBreakpoint: HeightBreakpoint;
}

const ViewportContext = createContext<ReadonlySize>({
  height: 0,
  width: 0,
  breakpoint: 'desktop',
  heightBreakpoint: 'tall',
});

const { media } = theme.config;

// `innerWidth` is an integer, so it can't place a fractional (zoomed) width the way CSS does.
const matchesMedia = (query: string) => 'matchMedia' in window && window.matchMedia(query).matches;

const useViewportSizeMonitor = (): ReadonlySize => {
  const calculateBreakpoint = (): Breakpoint => {
    if (matchesMedia(media.mobile)) {
      return 'mobile';
    }
    if (matchesMedia(media.desktop)) {
      return 'desktop';
    }
    return 'tablet';
  };

  const calculateHeightBreakpoint = (): HeightBreakpoint =>
    matchesMedia(media.short) ? 'short' : 'tall';

  const [size, setSize] = useState<ReadonlySize>({
    width: window.innerWidth,
    height: window.innerHeight,
    breakpoint: calculateBreakpoint(),
    heightBreakpoint: calculateHeightBreakpoint(),
  });

  const handleResize = useCallback(() => {
    setSize({
      width: window.innerWidth,
      height: window.innerHeight,
      breakpoint: calculateBreakpoint(),
      heightBreakpoint: calculateHeightBreakpoint(),
    });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return size;
};

/**
 * Measures the inner width and height of the window element and makes the dimensions available to
 * the {@link useViewport} hook through context.
 */
export function ViewportProvider({ children }: HasChildren) {
  const size = useViewportSizeMonitor();
  return <ViewportContext.Provider value={size}>{children}</ViewportContext.Provider>;
}

/**
 * @returns The inner width and height of the window element, representing the dimensions of the
 * user's viewport for use in responsive layouts.
 */
export const useViewport = (): ReadonlySize => useContext(ViewportContext);
